export const beforeItems = [
  'You look at your weld and aren’t sure what went wrong.',
  'You change your settings without knowing if they’re actually the problem.',
  'You watch three different YouTube videos and get three different recommendations.',
  'You repeat the same weld hoping it will somehow come out better.',
  'You waste electrodes, material and time trying to figure out what to change.',
  'Every new project starts with the same question: “What should I check first?”',
]

export const afterItems = [
  'You’ll have a clear process to follow before, during and after welding.',
  'You’ll be able to identify common visual problems faster.',
  'You’ll know what to check before randomly changing your settings.',
  'You’ll have quick-reference decision maps instead of searching through dozens of videos.',
  'You’ll be able to document what worked and build your own welding reference.',
  'You’ll stop starting from zero every time something looks off.',
]

export const forYouItems = [
  'You own a welder but still second-guess your decisions.',
  'You learn from YouTube, but every welder recommends something different.',
  'You look at a weld and aren’t sure if it’s technique, preparation or setup.',
  'You waste electrodes and material because you don’t know what to adjust.',
  'You want a simple process instead of more complicated welding theory.',
  'You want to troubleshoot problems without starting from zero every time.',
  'You want to build projects with greater confidence.',
  'You want a repeatable process instead of relying on trial and error.',
  'You want a practical reference you can consult from your phone, tablet or computer.',
]

export type Volume = {
  number: string
  title: string
  tagline: string
  body: string[]
  extra?: string
}

export const volumes: Volume[] = [
  {
    number: '01',
    title: 'THE 5-DECISION METHOD™',
    tagline: 'PREPARE → SET → WELD → CHECK → CORRECT',
    body: [
      'Discover the simple decision framework that organizes your welding process into five steps.',
      'Instead of wondering what to do next, follow a clear sequence from preparation to correction.',
    ],
  },
  {
    number: '02',
    title: 'PRE-WELD DECISION GUIDE™',
    tagline: 'Know what to check before striking the arc.',
    body: [
      'Use practical checklists and decision maps to review your material, preparation, workspace, tools and project before you start.',
      'The objective: eliminate avoidable mistakes before they cost you material.',
    ],
  },
  {
    number: '03',
    title: 'SETUP DECISION CODE™',
    tagline: 'Stop randomly changing settings.',
    body: [
      'Learn how to approach your welding setup through a structured decision process based on the variables you need to consider before welding.',
    ],
    extra: 'Includes quick-reference tables and personal settings worksheets.',
  },
  {
    number: '04',
    title: 'WELD CONTROL SYSTEM™',
    tagline: 'Know what to observe while you’re making the weld.',
    body: [
      'Learn how to pay attention to the visual and practical indicators that help you recognize inconsistencies while you’re working.',
    ],
    extra: 'Includes session checklists, observation sheets and consistency trackers.',
  },
  {
    number: '05',
    title: 'WELD DIAGNOSIS LAB™',
    tagline: 'SEE → IDENTIFY → CHECK → CORRECT',
    body: [
      'Your weld doesn’t look right? Instead of immediately grinding it away and starting again, use visual diagnosis maps to identify what you should investigate first.',
    ],
    extra: 'Includes defect comparison guides, symptom maps and troubleshooting matrices.',
  },
  {
    number: '06',
    title: 'CORRECTION & CONSISTENCY PROTOCOL™',
    tagline: 'Turn mistakes into information.',
    body: [
      'Document what happened, what you changed and what result you obtained so you stop repeating the same mistakes and gradually build your own welding reference system.',
    ],
    extra: 'Includes correction worksheets, practice logs and progress trackers.',
  },
]

export type Bonus = {
  number: string
  value: string
  title: string
  kicker: string
  body: string
  bullets?: string[]
  footer?: string
  image: string
  tag?: string
}

export const bonuses: Bonus[] = [
  {
    number: 'BONUS #1',
    value: '$47',
    title: 'WELD RESCUE CARDS™',
    kicker: '“DON’T SCRAP IT. DIAGNOSE IT.”',
    body: 'A collection of quick-reference troubleshooting cards for those moments when a weld doesn’t come out the way you expected.',
    bullets: ['WHAT YOU SEE → WHAT TO CHECK → WHAT TO INVESTIGATE NEXT'],
    footer: 'Perfect for keeping beside your welding station.',
    image: 'https://cdn.magicpatterns.com/uploads/h9dUFHFuWkraquXL84SvpU/BONUS_1_WELD-EN.png',
  },
  {
    number: 'BONUS #2',
    value: '$67',
    title: 'WELD BEAD VISUAL ATLAS™',
    kicker: 'COMPARE. IDENTIFY. CORRECT.',
    body: 'A visual reference library designed to help you compare different weld appearances and recognize common problems faster.',
    footer: 'Instead of asking “why does my weld look like this?”, you’ll have a visual reference to help you determine what to investigate.',
    image: 'https://cdn.magicpatterns.com/uploads/gY9t9f2U3itgsLmnpozETw/BONUS_2_WELD-EN.png',
  },
  {
    number: 'BONUS #3',
    value: '$37',
    title: 'WELD PROJECT BLUEPRINT™',
    kicker: 'PLAN THE WELD BEFORE YOU MAKE IT.',
    body: 'A practical project planning pack to organize your whole build.',
    bullets: [
      'Materials',
      'Tools',
      'Preparation',
      'Welding sequence',
      'Quality checks',
      'Finishing',
      'Project notes',
    ],
    footer: 'Stop starting projects without knowing what needs to happen next.',
    image: 'https://cdn.magicpatterns.com/uploads/7xK7CjSerQ9eqVch7mmSfG/BONUS_3_WELD-EN.png',
    tag: 'MOST USED',
  },
  {
    number: 'BONUS #4',
    value: '$57',
    title: 'WELD CONSISTENCY JOURNAL™',
    kicker: 'TURN EVERY PRACTICE SESSION INTO USEFUL DATA.',
    body: 'Track what you welded, what you changed, what happened, what went wrong, what worked and what you should repeat.',
    footer: 'Build your own personal welding reference over time.',
    image: 'https://cdn.magicpatterns.com/uploads/rhMKoCYGSiPaDxxf8cebEQ/BONUS_4_WELD-EN.png',
  },
  {
    number: 'BONUS #5',
    value: '$37',
    title: 'WORKSHOP DECISION MAP™',
    kicker: 'YOUR ONE-PAGE WELDING COMMAND CENTER.',
    body: 'A printable visual map showing the complete decision process: PREPARE → SET → WELD → CHECK → CORRECT.',
    footer: 'Keep it on your wall, bench or workshop so you always know where to start.',
    image: 'https://cdn.magicpatterns.com/uploads/j7aFmEHW2VxjwCL41eZC1n/BONUS_5_WELD-EN.png',
  },
]

export type InsideItem = { title: string; body: string; icon: string }

export const insideItems: InsideItem[] = [
  {
    title: '6 MASTER VOLUMES',
    body: 'Complete practical volumes designed for quick consultation before, during and after your welding sessions.',
    icon: 'book',
  },
  {
    title: '300+ PRACTICAL DECISIONS',
    body: 'Clear decision points organized around the situations you’re most likely to encounter in the workshop.',
    icon: 'list',
  },
  {
    title: 'VISUAL DECISION MAPS',
    body: 'Follow simple paths that help you determine what to check next instead of randomly changing variables.',
    icon: 'map',
  },
  {
    title: 'VISUAL ERROR-DIAGNOSIS GUIDES',
    body: 'Compare what you’re seeing with common welding problems and identify what you should investigate.',
    icon: 'search',
  },
  {
    title: 'QUICK-REFERENCE TABLES',
    body: 'Reference tables designed to help you make more informed decisions before and during your sessions.',
    icon: 'table',
  },
  {
    title: 'PRINTABLE CHECKLISTS',
    body: 'Simple checklists you can print and keep next to your welding station.',
    icon: 'check',
  },
  {
    title: 'WELDING SESSION TRACKERS',
    body: 'Record your settings, observations, mistakes and corrections so every session becomes useful information.',
    icon: 'clipboard',
  },
  {
    title: 'ACCESS FROM ANY DEVICE',
    body: 'Use the system from your phone, tablet or computer whenever you need it.',
    icon: 'device',
  },
]

export const testimonials = [
  {
    quote:
      'I stopped changing things randomly because now I have a process to follow when something doesn’t look right.',
    name: 'Rafael Domínguez',
    role: 'Welder',
    photo: 'https://cdn.magicpatterns.com/patterns/generated-images/058dd931-8f31-42f4-998d-55f86f3aa672.jpg',
  },
  {
    quote:
      'The decision maps make it much easier to know what to check instead of opening five different YouTube videos.',
    name: 'Marcos Herrera',
    role: 'DIY Welder',
    photo: 'https://cdn.magicpatterns.com/patterns/generated-images/648e4d1b-beef-42bf-88bc-f20e0025af99.jpg',
  },
  {
    quote:
      'The biggest difference is that I don’t immediately assume I need to start over. I can first work through the diagnosis.',
    name: 'Valeria Ortiz',
    role: 'Independent Welder',
    photo: 'https://cdn.magicpatterns.com/patterns/generated-images/59412958-d1ad-44cb-83de-c33e54792069.jpg',
  },
]

export const faqs = [
  {
    q: 'How will I receive Weld Decision System™?',
    a: 'Immediately after your payment, you’ll receive an email with access to the 6 Master Volumes and 5 Exclusive Bonuses in digital format.',
  },
  {
    q: 'Does it work if I’ve never welded before?',
    a: 'Yes. The system is designed to provide a structured reference for beginner and developing welders. It focuses on decisions, checklists and troubleshooting rather than assuming you already know everything.',
  },
  {
    q: 'What if I already have welding experience?',
    a: 'The system can also be used as a quick-reference and troubleshooting tool. Experienced welders can use the decision maps, checklists and tracking tools to organize their process and diagnose recurring problems.',
  },
  {
    q: 'Is this a welding course?',
    a: 'No. Weld Decision System™ is designed as a practical decision and reference system, not a long, theory-heavy video course.',
  },
  {
    q: 'Do I need internet access?',
    a: 'You can download and save the digital materials on your device for convenient reference. Some features may depend on the format/device you use.',
  },
  {
    q: 'Is this a one-time payment?',
    a: 'Yes. The launch offer is a one-time payment of $27 USD. There are no recurring subscription fees.',
  },
  {
    q: 'Do I get lifetime access?',
    a: 'Yes. Your purchase includes lifetime access to the product under the terms of the offer.',
  },
  {
    q: 'What happens if I don’t like it?',
    a: 'You are protected by the 7-Day Satisfaction Guarantee. If it isn’t what you expected, contact us within the applicable seven-day period to request a refund.',
  },
]
